import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

export type ReferenceObjectId = string;
export type ReferenceObjectType = 'box' | 'sphere' | 'cylinder' | 'triangularPrism' | 'stairs';

export type ReferenceObjectParameters = {
  width:number;
  height:number;
  depth:number;
  radius:number;
  segments:number;
  steps:number;
};

export type ReferenceObjectData = {
  id:ReferenceObjectId;
  name:string;
  type:ReferenceObjectType;
  parameters:ReferenceObjectParameters;
  position:[number, number, number];
  rotation:[number, number, number];
  scale:[number, number, number];
  color:string;
  opacity:number;
  visible:boolean;
  wireframe:boolean;
  castShadow:boolean;
  receiveShadow:boolean;
};

export type ReferenceObjectRecord = ReferenceObjectData & { object:THREE.Mesh };
export type ReferenceObjectLimits = { maxObjects:number };

export const referenceObjectLimits:ReferenceObjectLimits = { maxObjects:20 };

const defaults:Record<ReferenceObjectType, ReferenceObjectParameters> = {
  box:{ width:1, height:1, depth:1, radius:.5, segments:24, steps:6 },
  sphere:{ width:1, height:1, depth:1, radius:.5, segments:24, steps:6 },
  cylinder:{ width:1, height:1.5, depth:1, radius:.5, segments:24, steps:6 },
  triangularPrism:{ width:1.4, height:1, depth:1.5, radius:.5, segments:24, steps:6 },
  stairs:{ width:2, height:2, depth:4, radius:.5, segments:24, steps:8 },
};

export const referenceObjectLabels:Record<ReferenceObjectType, string> = {
  box:'立方体', sphere:'球体', cylinder:'圆柱', triangularPrism:'三棱柱', stairs:'楼梯',
};

function positive(value:number, fallback=.05) { return Number.isFinite(value) ? Math.max(fallback, value) : fallback; }

function triangularPrismGeometry(width:number, height:number, depth:number) {
  const w=width/2, d=depth/2;
  const vertices = new Float32Array([
    -w,0,-d, w,0,-d, -w,height,-d,
    -w,0,d,  w,0,d,  -w,height,d,
  ]);
  const indices = [0,1,2, 3,5,4, 0,3,4, 0,4,1, 0,2,5, 0,5,3, 1,4,5, 1,5,2];
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

function stairsGeometry(width:number, height:number, depth:number, count:number) {
  const steps = Math.max(1, Math.round(count));
  const stepHeight = height / steps;
  const stepDepth = depth / steps;
  const geometries:THREE.BufferGeometry[] = [];
  for (let index=0; index<steps; index+=1) {
    const currentHeight = stepHeight * (index + 1);
    const geometry = new THREE.BoxGeometry(width, currentHeight, stepDepth);
    geometry.translate(0, currentHeight / 2, -depth / 2 + stepDepth * (index + .5));
    geometries.push(geometry);
  }
  const merged = mergeGeometries(geometries, false)!;
  geometries.forEach((geometry) => geometry.dispose());
  return merged;
}

function createGeometry(type:ReferenceObjectType, raw:ReferenceObjectParameters) {
  const width=positive(raw.width), height=positive(raw.height), depth=positive(raw.depth), radius=positive(raw.radius);
  const segments=Math.min(64, Math.max(3, Math.round(raw.segments)));
  if (type === 'sphere') return new THREE.SphereGeometry(radius, Math.max(8, segments), Math.max(6, Math.round(segments*.65)));
  if (type === 'cylinder') return new THREE.CylinderGeometry(radius, radius, height, segments);
  if (type === 'triangularPrism') return triangularPrismGeometry(width, height, depth);
  if (type === 'stairs') return stairsGeometry(width, height, depth, Math.min(50, Math.max(1, Math.round(raw.steps))));
  return new THREE.BoxGeometry(width, height, depth).translate(0, height/2, 0);
}

export class ReferenceObjectManager {
  readonly records = new Map<ReferenceObjectId, ReferenceObjectRecord>();
  constructor(readonly group:THREE.Group, readonly limits:ReferenceObjectLimits = referenceObjectLimits) {}

  canCreate(amount=1) { return this.records.size + amount <= this.limits.maxObjects; }

  create(type:ReferenceObjectType, overrides:Partial<ReferenceObjectData> = {}) {
    if (!this.canCreate()) return null;
    const parameters={ ...defaults[type], ...overrides.parameters };
    const id=overrides.id ?? `prop_${crypto.randomUUID()}`;
    const material=new THREE.MeshStandardMaterial({ color:overrides.color ?? '#aeb5bf', roughness:.72, metalness:.03, transparent:true, opacity:overrides.opacity ?? .88, wireframe:overrides.wireframe ?? false });
    const object=new THREE.Mesh(createGeometry(type, parameters), material);
    object.userData.referenceObjectId=id;
    object.position.fromArray(overrides.position ?? [0,0,0]);
    object.rotation.fromArray([...(overrides.rotation ?? [0,0,0]), 'XYZ']);
    object.scale.fromArray(overrides.scale ?? [1,1,1]);
    object.visible=overrides.visible ?? true;
    object.castShadow=overrides.castShadow ?? true;
    object.receiveShadow=overrides.receiveShadow ?? true;
    const record:ReferenceObjectRecord={ id, name:overrides.name ?? `${referenceObjectLabels[type]} ${String(this.records.size+1).padStart(2,'0')}`, type, parameters, object, position:object.position.toArray() as [number,number,number], rotation:[object.rotation.x,object.rotation.y,object.rotation.z], scale:object.scale.toArray() as [number,number,number], color:material.color.getStyle(), opacity:material.opacity, visible:object.visible, wireframe:material.wireframe, castShadow:object.castShadow, receiveShadow:object.receiveShadow };
    this.records.set(id, record); this.group.add(object); return record;
  }

  get(id:ReferenceObjectId|null) { return id ? this.records.get(id) : undefined; }

  updateGeometry(id:ReferenceObjectId, changes:Partial<ReferenceObjectParameters>) {
    const record=this.records.get(id); if(!record)return;
    Object.assign(record.parameters, changes);
    const old=record.object.geometry;
    record.object.geometry=createGeometry(record.type, record.parameters);
    old.dispose();
  }

  duplicate(id:ReferenceObjectId) {
    const source=this.serializeOne(id); if(!source || !this.canCreate())return null;
    return this.create(source.type, { ...source, id:undefined, name:`${source.name} 副本`, position:[source.position[0]+.35,source.position[1],source.position[2]+.35] });
  }

  remove(id:ReferenceObjectId) {
    const record=this.records.get(id); if(!record)return false;
    this.group.remove(record.object); record.object.geometry.dispose(); (record.object.material as THREE.Material).dispose(); this.records.delete(id); return true;
  }

  clear() { [...this.records.keys()].forEach((id)=>this.remove(id)); }

  serializeOne(id:ReferenceObjectId):ReferenceObjectData|undefined {
    const record=this.records.get(id); if(!record)return;
    const material=record.object.material as THREE.MeshStandardMaterial;
    return { id:record.id,name:record.name,type:record.type,parameters:{...record.parameters},position:record.object.position.toArray() as [number,number,number],rotation:[record.object.rotation.x,record.object.rotation.y,record.object.rotation.z],scale:record.object.scale.toArray() as [number,number,number],color:`#${material.color.getHexString()}`,opacity:material.opacity,visible:record.object.visible,wireframe:material.wireframe,castShadow:record.object.castShadow,receiveShadow:record.object.receiveShadow };
  }

  serialize() { return [...this.records.keys()].map((id)=>this.serializeOne(id)!); }
  deserialize(items:ReferenceObjectData[]) { this.clear(); items.slice(0,this.limits.maxObjects).forEach((item)=>this.create(item.type,item)); }
}
